/** @jsxImportSource @emotion/react */
import { Box, Button, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

import { OGR_ACCEPT_ATTR } from '@app/modules/Home/Data/constants/ogr.formats'
import { fileRow } from './DataUpload.styles'

interface FilePickerFieldProps {
  inputRef: React.RefObject<HTMLInputElement | null>
  selectedFiles: File[]
  onFilesChange: (files: File[]) => void
  error?: string
  accept?: string
  buttonLabel?: string
  disabled?: boolean
  sx?: SxProps<Theme>
  rhfRef?: (el: HTMLInputElement | null) => void
  rhfInputProps?: Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'type' | 'multiple' | 'accept' | 'style' | 'ref'
  >
}

const FilePickerField = ({
  inputRef,
  selectedFiles,
  onFilesChange,
  error,
  accept = OGR_ACCEPT_ATTR,
  buttonLabel,
  disabled,
  sx,
  rhfRef,
  rhfInputProps,
}: FilePickerFieldProps) => {
  const { onChange: rhfOnChange, ...spreadInputProps } = rhfInputProps ?? {}
  const label = buttonLabel ?? (selectedFiles.length > 0 ? 'Change file(s)' : 'Choose file(s)')

  return (
    <Box css={fileRow} sx={sx}>
      <Button
        component="label"
        variant="outlined"
        color={error ? 'error' : 'primary'}
        disabled={disabled}
      >
        {label}
        <input
          {...spreadInputProps}
          ref={(el) => {
            rhfRef?.(el)
            ;(inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el
          }}
          type="file"
          multiple
          accept={accept}
          style={{ display: 'none' }}
          onChange={(e) => {
            onFilesChange(Array.from(e.target.files ?? []))
            rhfOnChange?.(e)
          }}
        />
      </Button>

      {selectedFiles.length > 0 ? (
        <Box>
          <Typography variant="body2" color="text.secondary">
            {selectedFiles.length === 1
              ? selectedFiles[0].name
              : `${selectedFiles.length} files selected`}
          </Typography>
          {selectedFiles.length > 1 && (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {selectedFiles.map((f) => (
                <Typography key={f.name} component="li" variant="caption" color="text.secondary">
                  {f.name}
                </Typography>
              ))}
            </Box>
          )}
          {error && (
            <Typography variant="body2" color="error.main">
              {error}
            </Typography>
          )}
        </Box>
      ) : error ? (
        <Typography variant="body2" color="error.main">
          {error}
        </Typography>
      ) : null}
    </Box>
  )
}

export default FilePickerField
